import { useCallback, useEffect, useRef, useState } from 'react';
import VideoService from '../service/VideoService';
import ResourceHelper from '../utils/ResourceHelper';
import { v4 as uuidv4 } from 'uuid';

export const THUMBNAIL_EDITING_MODE = {
  STATIC: 'static',
  ANIMATED: 'animated',
};

export default function useThumbnailChooserHook({ video, setVideo }) {
  const [customThumbnailModal, setCustomThumbnailModal] = useState(false);
  const [cancelThumbnailGenerating, setCancelThumbnailGenerating] = useState(false);
  const [processingThumbnail, setProcessingThumbnail] = useState(false);
  const [customStaticLoading, setCustomStaticLoading] = useState(false);
  const [customAnimatedLoading, setCustomAnimatedLoading] = useState(false);
  const [thumbnailJobId, setThumbnailJobId] = useState([]);

  const timestampsRef = useRef();

  const closeThumbnailModal = useCallback(() => {
    if (customStaticLoading || customAnimatedLoading) {
      setCancelThumbnailGenerating(true);
    }
    setCustomThumbnailModal(false);
    setProcessingThumbnail(false);
  }, [customThumbnailModal, customStaticLoading, customAnimatedLoading]);

  const openThumbnailModal = () => {
    setCustomThumbnailModal(true);
  };

  const onGenerationSuccess = useCallback(
    async (mode, jobDetails) => {
      let output = jobDetails.outputs[0];
      const videoDetails = await VideoService.getVideoDetails(video.id); // We need the new Resource IDs, which are not present in the jobDetails output
      handleUpdateThumbnail(
        output.url.endsWith('.gif') ? THUMBNAIL_EDITING_MODE.ANIMATED : THUMBNAIL_EDITING_MODE.STATIC,
        videoDetails
      );
    },
    [video, handleUpdateThumbnail]
  );

  const onSelect = useCallback(
    async data => {
      setProcessingThumbnail(true);
      const filenameBase = uuidv4();
      let relatedTo = {
        model: 'story',
        id: video.id,
      };
      // Define static and animated thumbnails objects
      const staticThumbnail = {
        label: 'customStaticThumbnail',
        filename: `${filenameBase}.jpg`,
        timestamp: data.static,
      };
      const animatedThumbnail = {
        label: 'customAnimatedThumbnail',
        filename: `${filenameBase}.gif`,
        timestamp: data.animated,
      };
      timestampsRef.current = { ...data };
      if (data.animated && data.static) {
        // Both static and animated thumbnails will be sent to backend to generate thumbnails
        setCustomAnimatedLoading(true);
        setCustomStaticLoading(true);

        let jobIdStatic = await VideoService.generateThumbnail(
          video,
          staticThumbnail.label,
          staticThumbnail.filename,
          staticThumbnail.timestamp,
          relatedTo
        );
        let jobIdAnimated = await VideoService.generateThumbnail(
          video,
          animatedThumbnail.label,
          animatedThumbnail.filename,
          animatedThumbnail.timestamp,
          relatedTo
        );
        setThumbnailJobId([jobIdStatic, jobIdAnimated]);
        if (!jobIdStatic && !jobIdAnimated) {
          setCustomAnimatedLoading(false);
          setCustomStaticLoading(false);
          console.error('Unable to start job');
        } else {
          await ResourceHelper.getJobDetails(jobIdStatic, onGenerationSuccess);
          await ResourceHelper.getJobDetails(jobIdAnimated, onGenerationSuccess);
        }
        return;
      } else {
        const { label, filename, timestamp } = data.animated ? animatedThumbnail : staticThumbnail;
        if (data.animated) {
          setCustomAnimatedLoading(true);
        } else {
          setCustomStaticLoading(true);
        }

        let jobId = await VideoService.generateThumbnail(video, label, filename, timestamp, relatedTo); // jobId is used for polling the job status
        setThumbnailJobId(prevState => [...prevState, jobId]);
        if (!jobId) {
          setCustomStaticLoading(false);
          setCustomAnimatedLoading(false);
          console.error('Unable to start job');
        } else {
          await ResourceHelper.getJobDetails(jobId, onGenerationSuccess);
        }
      }
    },
    [video]
  );

  // Update thumbnail depends on mode - animated or static
  const handleUpdateThumbnail = async (mode, videoDetails) => {
    const animatedMode = mode === THUMBNAIL_EDITING_MODE.ANIMATED;
    let thumbnail = ResourceHelper.getResourceByLabel(
      videoDetails,
      animatedMode ? 'customAnimatedThumbnail' : 'customStaticThumbnail'
    );
    if (thumbnail) {
      await ResourceHelper.updateResource(thumbnail?.id, {
        properties: { timestamp: animatedMode ? timestampsRef.current.animated : timestampsRef.current.static },
      });
      const resourcesToUpdate = structuredClone(videoDetails.resources);
      for (const key in resourcesToUpdate) {
        if (resourcesToUpdate[key].label === (animatedMode ? 'customAnimatedThumbnail' : 'customStaticThumbnail')) {
          resourcesToUpdate[key].properties = {
            timestamp: animatedMode ? timestampsRef.current.animated : timestampsRef.current.static,
          };
        }
      }
      animatedMode ? setCustomAnimatedLoading(false) : setCustomStaticLoading(false);
      setVideo(prevState => ({
        ...prevState,
        resources: resourcesToUpdate,
        ...(animatedMode ? {} : { thumbnail: thumbnail.url }),
      }));
    }
  };

  const onCancelThumbnailGeneration = () => {
    setCancelThumbnailGenerating(true);
  };

  useEffect(() => {
    if (cancelThumbnailGenerating) {
      thumbnailJobId.forEach(jobId => {
        ResourceHelper.cancelJob(jobId);
      });
      setCustomStaticLoading(false);
      setCustomAnimatedLoading(false);
      setCancelThumbnailGenerating(false);
    }
  }, [cancelThumbnailGenerating]);

  useEffect(() => {
    if (processingThumbnail && !customStaticLoading && !customAnimatedLoading) {
      closeThumbnailModal();
    }
  }, [customStaticLoading, customAnimatedLoading, processingThumbnail]);

  return {
    customThumbnailModal,
    closeThumbnailModal,
    openThumbnailModal,
    onCancelThumbnailGeneration,
    onSelect,
    processingThumbnail,
    customStaticLoading,
    customAnimatedLoading,
  };
}
